import { Component, Input, OnChanges, SimpleChanges, OnInit, Optional } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

interface RoleItem {
  id?: string | number;
  name: string;
  selected?: boolean;
}

@Component({
  selector: 'app-role-picker',
  templateUrl: './role-picker.component.html',
  standalone: false,
})
export class RolePickerComponent implements OnChanges, OnInit {
  @Input() availableItems: any[] = [];
  @Input() selectedItems: any[] = [];
  @Input() displayField: string = 'name';
  @Input() idField: string = 'id';
  // Habilita, apenas quando explicitamente ligado pela tela consumidora, o toggle de
  // busca em massa (colar múltiplas keys separadas por vírgula) no campo "Available".
  // Desabilitado por padrão para não afetar nenhuma outra tela que reutiliza o role-picker.
  @Input() allowBulkSearch: boolean = false;

  // Local copies normalized to RoleItem
  available: RoleItem[] = [];
  selected: RoleItem[] = [];

  // Loading state for async population
  loading: boolean = false;

  // Searches
  searchAvailable: string = '';
  searchSelected: string = '';

  // Estado do toggle de busca em massa (somente tem efeito quando allowBulkSearch=true).
  bulkSearchMode: boolean = false;
  // Feedback textual da última busca em massa (ex.: "3 of 5 keys found and selected").
  bulkSearchFeedback: string = '';
  // Termos (keys) marcados pela última execução da busca em massa — usado para só
  // ajustar o que mudou de uma busca para a outra, sem sobrescrever seleção manual.
  private lastBulkTerms: Set<string> = new Set();

  // Ao trocar de modo, zera o campo de busca (evita texto com vírgulas sobrando no
  // filtro padrão) e também os checkboxes que a busca em massa tinha marcado: sem
  // isso, permissões marcadas mas fora da tela (filtro padrão não as mostra mais)
  // ficariam selecionadas "escondidas" e iriam junto na próxima vez que a seta for
  // usada, mesmo sem o feedback visual que indicava a marcação em massa.
  toggleBulkSearchMode(): void {
    if (!this.allowBulkSearch) return;
    this.bulkSearchMode = !this.bulkSearchMode;
    this.searchAvailable = '';
    this.bulkSearchFeedback = '';
    this.available.forEach((a) => (a.selected = false));
    this.lastBulkTerms = new Set();
  }

  // Chave usada para correspondência exata na busca em massa: prioriza o idField
  // (a permission key real), caindo para o texto exibido quando não houver idField.
  private bulkKeyOf(item: RoleItem): string {
    return String(item.id ?? item.name).toLowerCase();
  }

  // Extrai os termos (keys) do texto colado/digitado, separados por vírgula.
  private parseBulkTerms(): string[] {
    return Array.from(
      new Set(
        (this.searchAvailable || '')
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter((t) => t.length > 0),
      ),
    );
  }

  // Interpreta o texto colado/digitado como termos separados por vírgula e, quando houver
  // correspondência exata de key, apenas MARCA o checkbox da permissão em "Available"
  // (não move para "Selected") — mover para o outro lado continua sendo escolha do usuário
  // via o botão de seta já existente. Isolado por allowBulkSearch/bulkSearchMode: sem
  // efeito em nenhuma outra tela ou modo.
  onBulkSearchInput(): void {
    if (!this.allowBulkSearch || !this.bulkSearchMode) {
      this.bulkSearchFeedback = '';
      return;
    }

    const terms = this.parseBulkTerms();
    const termSet = new Set(terms);

    // Ajusta só o que mudou desde a última busca: marca quem entrou na lista de
    // termos, desmarca quem saiu dela, e preserva o que o usuário decidiu na mão
    // para as keys que continuam na lista — a seleção automática não sobrescreve
    // uma desmarcação (ou marcação) manual feita entre uma tecla e outra.
    this.available.forEach((a) => {
      const key = this.bulkKeyOf(a);
      const isTerm = termSet.has(key);
      const wasTerm = this.lastBulkTerms.has(key);
      if (isTerm && !wasTerm) {
        a.selected = true;
      } else if (!isTerm && wasTerm) {
        a.selected = false;
      }
    });
    this.lastBulkTerms = termSet;

    if (terms.length === 0) {
      this.bulkSearchFeedback = '';
      return;
    }

    const alreadySelectedTerms = this.selected
      .map((s) => this.bulkKeyOf(s))
      .filter((key) => termSet.has(key));
    const matchedInAvailable = this.available
      .filter((a) => a.selected && termSet.has(this.bulkKeyOf(a)))
      .map((a) => this.bulkKeyOf(a));

    const foundTerms = new Set([...alreadySelectedTerms, ...matchedInAvailable]);
    this.bulkSearchFeedback = `${foundTerms.size} of ${terms.length} keys found and selected`;
  }

  private initialSnapshot: string = '';
  isDialog: boolean = false;
  title: string = 'Manage Roles';

  constructor(
    @Optional() private ref?: DynamicDialogRef,
    @Optional() private config?: DynamicDialogConfig,
  ) {}

  ngOnInit(): void {
    this.isDialog = this.shouldRenderDialogLayout();
    if (this.config?.data) {
      // allow opening via dialog with data
      this.title = this.config.data.title || this.title;
      // Permite customizar o campo exibido/identificador também via dialog
      // (mesma flexibilidade dos usos via template).
      if (this.config.data.displayField) this.displayField = this.config.data.displayField;
      if (this.config.data.idField) this.idField = this.config.data.idField;
      if (this.config.data.availableItems) this.availableItems = this.config.data.availableItems;
      if (this.config.data.selectedItems) this.selectedItems = this.config.data.selectedItems;
      this.normalizeInputs();
      this.sortList(this.available);
      this.sortList(this.selected);
      // take snapshot for dialog dirty checking
      setTimeout(() => this.takeSnapshot(), 50);
    }
  }

  private shouldRenderDialogLayout(): boolean {
    if (!this.ref || !this.config?.data) {
      return false;
    }

    const data = this.config.data;
    return (
      Array.isArray(data.availableItems) ||
      Array.isArray(data.selectedItems) ||
      !!data.title
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availableItems'] || changes['selectedItems']) {
      this.normalizeInputs();
      this.sortList(this.available);
      this.sortList(this.selected);
      // Reaplica o match da busca em massa: normalizeInputs() recria os RoleItem[]
      // do zero, então os checkboxes marcados pela busca em massa seriam perdidos
      // se availableItems/selectedItems mudarem enquanto o modo está ativo. Zera
      // lastBulkTerms antes: os objetos são novos, então não há seleção manual
      // "anterior" a preservar — a comparação precisa recomeçar do zero.
      if (this.allowBulkSearch && this.bulkSearchMode) {
        this.lastBulkTerms = new Set();
        this.onBulkSearchInput();
      }
    }
  }

  normalizeInputs() {
    this.available = (this.availableItems || []).map((it) => this.toRoleItem(it));
    this.selected = (this.selectedItems || []).map((it) => this.toRoleItem(it));
  }

  private toRoleItem(it: any): RoleItem {
    if (it == null) return { name: '' };
    if (typeof it === 'string') return { name: it, selected: !!(it as any).selected };
    const name = this.displayField && it[this.displayField] ? it[this.displayField] : it.name || '';
    const id = this.idField && it[this.idField] !== undefined ? it[this.idField] : undefined;
    return { id, name, selected: !!(it as any).selected };
  }

  // Moving
  moveToSelected() {
    const itemsToMove = this.available.filter((r) => r.selected);
    this.available = this.available.filter((r) => !r.selected);
    itemsToMove.forEach((i) => { i.selected = false; this.selected.push(i); });
    this.sortList(this.selected);
  }

  moveToAvailable() {
    const itemsToMove = this.selected.filter((r) => r.selected);
    this.selected = this.selected.filter((r) => !r.selected);
    itemsToMove.forEach((i) => { i.selected = false; this.available.push(i); });
    this.sortList(this.available);
  }

  hasSelected(list: RoleItem[]): boolean {
    return list.some((r) => r.selected);
  }

  isAllSelected(list: RoleItem[]): boolean {
    return list.length > 0 && list.every((r) => r.selected);
  }

  toggleSelectAll(list: RoleItem[], event: any) {
    const isChecked = event.target.checked;
    list.forEach((r) => (r.selected = isChecked));
  }

  private sortList(list: RoleItem[]) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  // Filtering helpers used by template
  filteredAvailable(): RoleItem[] {
    // Branch isolado: só entra em modo "lista de keys" quando a tela habilitou o
    // toggle (allowBulkSearch) e ele está ativo. Caso contrário, comportamento inalterado.
    if (this.allowBulkSearch && this.bulkSearchMode) {
      return this.filteredAvailableBulk();
    }
    if (!this.searchAvailable) return this.available;
    const s = this.searchAvailable.toLowerCase();
    return this.available.filter((i) => i.name.toLowerCase().includes(s));
  }

  private filteredAvailableBulk(): RoleItem[] {
    const terms = this.parseBulkTerms();
    if (terms.length === 0) return this.available;
    const termSet = new Set(terms);
    return this.available.filter((i) => termSet.has(this.bulkKeyOf(i)));
  }

  filteredSelected(): RoleItem[] {
    if (!this.searchSelected) return this.selected;
    const s = this.searchSelected.toLowerCase();
    return this.selected.filter((i) => i.name.toLowerCase().includes(s));
  }

  // Snapshot/dirty checks
  takeSnapshot() {
    this.initialSnapshot = JSON.stringify(this.selected.map(s => s.id ?? s.name).sort());
  }

  hasChanges(): boolean {
    const cur = JSON.stringify(this.selected.map(s => s.id ?? s.name).sort());
    return cur !== this.initialSnapshot;
  }

  // Accessors for parent
  getSelectedRaw(): any[] {
    return this.selected.map(s => (this.idField ? s.id ?? s.name : s.name));
  }

  // Dialog helpers
  onSaveDialog() {
    if (this.ref) {
      this.ref.close({ roleNames: this.getSelectedRaw() });
    }
  }

  closeDialog() {
    this.ref?.close();
  }
}
